import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React from "react";
import { addSuspectToWitnessFeedback } from "services/caseServices";
import { Roles, SuspectTowitness, SuspectToWitnessFeedback } from "types";
import { feedbackIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";
import { useTranslation } from "react-i18next";

type Props = {
  conversion: SuspectTowitness;
  respond_back_comments: SuspectToWitnessFeedback[];
  setRefetch: () => void;
};

const SuspectToWitnessFeedbacks: React.FC<Props> = ({
  conversion,
  respond_back_comments,
  setRefetch,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isAttorney = hasRole(loginData?.roles, [Roles.generalAttorney]);

  const [newComment, setNewComment] = React.useState<string>("");
  const [respondbackFile, setRespondbackFile] = React.useState<File | null>(
    null,
  );

  return (
    <div>
      <h1 className="text-black font-semibold text-lg flex flex-row gap-2 items-center mb-2">
        <span className="text-[#AA232F] text-xl">{feedbackIcon}</span>
        <span className="tracking-wide">{t("feedback_section")}</span>
      </h1>
      <div className="flex flex-col gap-4 mt-2 justify-between h-[70vh] w-full">
        <div className="flex flex-col overflow-y-auto gap-3 pr-1">
          {respond_back_comments && respond_back_comments.length > 0 ? (
            respond_back_comments?.map((feedback, index) => (
              <div
                key={index}
                className={`flex flex-row items-start gap-3 rounded-lg shadow-sm border border-gray-200 px-4 py-3 ${
                  index % 2 === 0 ? "bg-[#F7F7F7]" : "bg-white"
                }`}
                style={{ overflowWrap: "break-word" }}
              >
                <div className="flex-1">
                  <div className="flex flex-row gap-2 items-center mb-1">
                    <span className="text-xs text-gray-400"></span>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold text-white ${
                        feedback.type === "initial"
                          ? "bg-blue-500"
                          : "bg-red-500"
                      }`}
                    >
                      {feedback.type === "initial"
                        ? t("initial")
                        : t("revocation")}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-1">
                    {feedback.comment}
                  </p>
                  <span className="text-[10px] text-gray-500 mt-1 capitalize">
                    {t("submitted_by")} {feedback.submitted_by}
                  </span>
                  {feedback.respondback_feedback && (
                    <div className="mt-2 border rounded-lg p-3 flex items-center gap-4 bg-[#F8F8F8]">
                      <img
                        src={require("../../../images/imageVector.png")}
                        alt={t("pdf")}
                        className="w-8 h-8"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-base text-[#0A1D39]">
                          {feedback.respondback_feedback instanceof File
                            ? feedback.respondback_feedback.name
                            : typeof feedback.respondback_feedback === "string"
                              ? (feedback.respondback_feedback as string)
                                  .split("/")
                                  .pop()
                              : t("file")}
                        </p>
                        <p className="text-xs text-gray-800">
                          {feedback.respondback_feedback instanceof File
                            ? `${Math.round(
                                feedback.respondback_feedback.size / 1024,
                              )}kb`
                            : ""}
                        </p>
                      </div>
                      {(() => {
                        const href =
                          typeof feedback.respondback_feedback === "string"
                            ? feedback.respondback_feedback
                            : feedback.respondback_feedback instanceof File
                              ? URL.createObjectURL(
                                  feedback.respondback_feedback,
                                )
                              : undefined;
                        if (href) {
                          return (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#212121] text-sm font-semibold border px-4 py-1 rounded hover:bg-gray-100"
                            >
                              {t("view")}
                            </a>
                          );
                        } else {
                          return (
                            <span className="text-gray-800 text-xs font-semibold cursor-not-allowed">
                              {t("no_file_available")}
                            </span>
                          );
                        }
                      })()}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-800 py-8">
              {t("no_feedback_yet")}
            </div>
          )}
        </div>

        {(isCoordinator || isAttorney) &&
          conversion?.decision !== "Rejected" && (
            <div
              style={{ boxShadow: "rgb(217 217 217 / 90%) 0px -6px 7px 0px" }}
              className="px-6 py-4 w-full bg-white rounded-lg border border-gray-200 mt-2"
            >
              <p className="font-semibold text-[#AA232F] mb-2">
                {/* {statusLabels["respond_back"]} */}
              </p>
              <div className="flex flex-col mt-2">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("respondback_feedback_file_pdf")}
                </label>
                <input
                  type="file"
                  className="mb-2"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setRespondbackFile(file);
                  }}
                />
                <textarea
                  rows={4}
                  className="w-full bg-[#F2F2F2] px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#AA232F] text-sm resize-none"
                  placeholder={t("write_your_feedback_here")}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <div className="text-right mt-2">
                  <button
                    className="px-5 py-2 rounded-full bg-[#AA232F] text-white text-xs font-semibold shadow hover:bg-[#E90101] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#AA232F]"
                    onClick={() => {
                      if (!newComment.trim()) {
                        showTooltip(t("please_enter_a_comment"), "error");
                        return;
                      }
                      addSuspectToWitnessFeedback({
                        type: conversion?.revocation_reason
                          ? "revocation"
                          : "initial",
                        respondback_feedback: respondbackFile,
                        comment: newComment,
                        submitted_by: loginData?.full_name || "",
                        conversion: conversion?.id,
                      })
                        .then(() => {
                          setNewComment("");
                          setRespondbackFile(null);
                          setRefetch();
                          showTooltip(
                            t("comment_added_successfully"),
                            "success",
                          );
                        })
                        .catch((error) => {
                          showTooltip(
                            errorHandlerOnForm(error) ||
                              t("error_adding_comment"),
                            "error",
                          );
                        });
                    }}
                  >
                    {t("send")}
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default SuspectToWitnessFeedbacks;
